import {
  AudioLines,
  Check,
  ChevronDown,
  ChevronUp,
  Loader2,
  Play,
  RotateCcw,
  Sparkles,
  Square,
  Trash2,
} from "lucide-react";
import { ELEVEN_LABS_VOICES } from "@/constants/voices";

type AiProvider = "eleven_labs" | "qwen_local";

export type ScenarioItem = {
  id: number;
  title: string;
  isOpen: boolean;
  aiProvider: AiProvider;
  prompt: string;
  targetAudience: string;
  narrativeTone: string;
  sourceMatch: number;
  durationSeconds: number;
  ttsVoiceId: string;
};

const aiOptions = [
  {
    id: "eleven_labs" as const,
    label: "Eleven Labs",
    description: "Voix expressives, qualité élevée. Échantillon généré après le choix.",
  },
  {
    id: "qwen_local" as const,
    label: "Qwen local",
    description: "Plus de personnalisation, échantillon généré en fin d'artefact.",
  },
];

function getStepFromSourceMatch(value: number): number {
  if (value > 66) return value >= 90 ? 0 : 1;
  if (value > 33) return 2;
  return value <= 5 ? 4 : 3;
}

function getSourceMatchFromStep(step: number): number {
  switch (step) {
    case 0: return 100;
    case 1: return 83;
    case 2: return 50;
    case 3: return 17;
    case 4: return 0;
    default: return 50;
  }
}

function formatSecondsLabel(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  const parts: string[] = [];
  if (mins > 0) parts.push(`${mins} minute${mins > 1 ? "s" : ""}`);
  if (secs > 0 || mins === 0) parts.push(`${secs} seconde${secs > 1 ? "s" : ""}`);
  return parts.join(" ");
}

function formatTimeMMSS(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export type ScenarioItemCardProps = {
  scenario: ScenarioItem;
  maxChars: number;
  canDelete: boolean;
  showAiDropdown: boolean;
  showAudienceDropdown: boolean;
  showToneDropdown: boolean;
  showVoiceDropdown: boolean;
  displayAudienceOptions: string[];
  displayToneOptions: string[];
  voiceInstructions: string;
  playingVoiceId: string | null;
  loadingPreviewId: string | null;
  onUpdate: (id: number, patch: Partial<ScenarioItem>) => void;
  onDelete: (id: number) => void;
  onToggleAiDropdown: (id: number | null) => void;
  onToggleAudienceDropdown: (id: number | null) => void;
  onToggleToneDropdown: (id: number | null) => void;
  onToggleVoiceDropdown: (id: number | null) => void;
  onVoiceInstructionsChange: (v: string) => void;
  onPlayPreview: (e: React.MouseEvent, voiceId: string) => void;
};

export function ScenarioItemCard({
  scenario,
  maxChars,
  canDelete,
  showAiDropdown,
  showAudienceDropdown,
  showToneDropdown,
  showVoiceDropdown,
  displayAudienceOptions,
  displayToneOptions,
  voiceInstructions,
  playingVoiceId,
  loadingPreviewId,
  onUpdate,
  onDelete,
  onToggleAiDropdown,
  onToggleAudienceDropdown,
  onToggleToneDropdown,
  onToggleVoiceDropdown,
  onVoiceInstructionsChange,
  onPlayPreview,
}: ScenarioItemCardProps) {

  return (
    <article className="flex flex-col gap-6 rounded-[18px] border border-[#8EA4BD] bg-white p-[25px] shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[16px] font-medium leading-none text-[#0F172B]">{scenario.title}</h3>
        <div className="inline-flex items-center gap-3">
          <button
            type="button"
            onClick={() => onUpdate(scenario.id, { isOpen: !scenario.isOpen })}
            className="inline-flex h-[38px] items-center gap-2 rounded-xl border border-[#E2E8F0] bg-transparent px-3 text-sm font-medium text-[#45556C] transition-colors hover:bg-[#F8FAFC]"
          >
            <span>Paramètres</span>
            {scenario.isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => onDelete(scenario.id)}
            disabled={!canDelete}
            className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-xl border border-[#E2E8F0] bg-white text-[#FF3B30] transition-colors hover:bg-[#fff1f0] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Supprimer scénario"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-[14px] font-medium leading-5 text-[#0F172B]">Prompt</label>
        <div
          className="flex min-h-[180px] cursor-text flex-col rounded-lg border border-[#E2E8F0] px-3 py-2"
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName !== "TEXTAREA")
              (e.currentTarget.querySelector("textarea") as HTMLTextAreaElement)?.focus();
          }}
        >
          <textarea
            value={scenario.prompt}
            onChange={(e) => onUpdate(scenario.id, { prompt: e.target.value.slice(0, maxChars) })}
            placeholder="Ex. : Présente son histoire, son apprentissage dans une première partie, l'évolution de son métier à travers le temps dans une seconde..."
            className="flex-1 w-full resize-y bg-transparent text-[14px] font-normal leading-5 text-[#45556C] outline-none placeholder:text-[#45556C] min-h-[140px]"
            rows={8}
          />
          <p className="text-right text-[14px] font-normal leading-none text-[#45556C]">
            {scenario.prompt.length}/{maxChars} caractères
          </p>
        </div>
      </div>

      {scenario.isOpen && (
        <>
          {/* Audience & Tone */}
          <div className="grid grid-cols-2 gap-6">
            <div className="relative flex flex-col gap-2">
              <label className="text-[14px] font-medium leading-5 text-[#0F172B]">Cible public</label>
              <button
                type="button"
                onClick={() => {
                  onToggleAudienceDropdown(showAudienceDropdown ? null : scenario.id);
                  onToggleToneDropdown(null);
                }}
                className="inline-flex h-9 w-full items-center justify-between rounded-lg border border-[#E2E8F0] bg-[#F4F4F4] px-3 text-[14px] font-normal text-[#45556C]"
              >
                <span>{scenario.targetAudience}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {showAudienceDropdown && (
                <div className="absolute left-0 top-[68px] z-20 w-full overflow-hidden rounded-lg border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
                  {displayAudienceOptions.map((option, index) => (
                    <button
                      key={`${option}-${index}`}
                      type="button"
                      onClick={() => {
                        onUpdate(scenario.id, { targetAudience: option });
                        onToggleAudienceDropdown(null);
                      }}
                      className={`w-full px-3 py-2 text-left text-[14px] text-[#45556C] transition-colors hover:bg-[#F8FAFC] ${
                        index < displayAudienceOptions.length - 1 ? "border-b border-[#E2E8F0]" : ""
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative flex flex-col gap-2">
              <label className="text-[14px] font-medium leading-5 text-[#0F172B]">Ton narratif</label>
              <button
                type="button"
                onClick={() => {
                  onToggleToneDropdown(showToneDropdown ? null : scenario.id);
                  onToggleAudienceDropdown(null);
                }}
                className="inline-flex h-9 w-full items-center justify-between rounded-lg border border-[#E2E8F0] bg-[#F4F4F4] px-3 text-[14px] font-normal text-[#45556C]"
              >
                <span>{scenario.narrativeTone}</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              {showToneDropdown && (
                <div className="absolute left-0 top-[68px] z-20 w-full overflow-hidden rounded-lg border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
                  {displayToneOptions.map((option, index) => (
                    <button
                      key={`${option}-${index}`}
                      type="button"
                      onClick={() => {
                        onUpdate(scenario.id, { narrativeTone: option });
                        onToggleToneDropdown(null);
                      }}
                      className={`w-full px-3 py-2 text-left text-[14px] text-[#45556C] transition-colors hover:bg-[#F8FAFC] ${
                        index < displayToneOptions.length - 1 ? "border-b border-[#E2E8F0]" : ""
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Source match slider */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[14px] font-medium leading-5 text-[#0F172B]">
              <span>Correspondance à la source audio</span>
              <button
                type="button"
                onClick={() => onUpdate(scenario.id, { sourceMatch: 50 })}
                className="inline-flex items-center gap-1 text-[#45556C] hover:text-[#0F172B]"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                <span>Réinitialiser</span>
              </button>
            </div>
            <div className="relative flex h-[52px] w-full items-center justify-between rounded-xl bg-[#F4F4F4] px-6 select-none">
              <span className="text-[14px] font-medium text-[#45556C]">Identique</span>
              <div className="relative mx-6 flex-1 h-[6px]">
                <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-[#E2E8F0]" />
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-[#007AFF]"
                  style={{ width: `${getStepFromSourceMatch(scenario.sourceMatch) * 25}%` }}
                />
                {[1, 2, 3].map((step) => {
                  const isActive = step <= getStepFromSourceMatch(scenario.sourceMatch);
                  const isThumbHere = getStepFromSourceMatch(scenario.sourceMatch) === step;
                  if (isThumbHere) return null;
                  return (
                    <div
                      key={step}
                      className={`absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors ${
                        isActive ? "bg-[#007AFF]" : "bg-[#CBD5E1]"
                      }`}
                      style={{ left: `${step * 25}%` }}
                    />
                  );
                })}
                <div
                  className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
                  style={{ left: `${getStepFromSourceMatch(scenario.sourceMatch) * 25}%` }}
                />
              </div>
              <span className="text-[14px] font-medium text-[#45556C]">Réinterprété</span>
              <input
                type="range"
                min="0"
                max="4"
                step="1"
                value={getStepFromSourceMatch(scenario.sourceMatch)}
                onChange={(e) => {
                  const step = parseInt(e.target.value, 10);
                  onUpdate(scenario.id, { sourceMatch: getSourceMatchFromStep(step) });
                }}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </div>
            <p className="text-[14px] font-normal leading-normal text-[#45556C] mt-1">
              Détermine l&apos;importance de la transcription dans le récit.
            </p>
          </div>

          {/* Duration slider */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[14px] font-medium leading-5 text-[#0F172B]">
              <span>Durée audio (minutes)</span>
              <span className="font-bold text-[#0F172B]">
                {formatSecondsLabel(scenario.durationSeconds)}
              </span>
            </div>
            <div className="relative flex h-[52px] w-full items-center justify-between rounded-xl bg-[#F4F4F4] px-6 select-none">
              <span className="text-[14px] font-medium text-[#45556C]">0:00</span>
              <div className="relative mx-6 flex-1 h-[6px]">
                <div className="absolute inset-y-0 left-0 right-0 rounded-full bg-[#E2E8F0]" />
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-[#007AFF]"
                  style={{ width: `${(scenario.durationSeconds / Math.max(300, scenario.durationSeconds)) * 100}%` }}
                />
                <div
                  className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.15)]"
                  style={{ left: `${(scenario.durationSeconds / Math.max(300, scenario.durationSeconds)) * 100}%` }}
                />
              </div>
              <span className="text-[14px] font-medium text-[#45556C]">
                {formatTimeMMSS(Math.max(300, scenario.durationSeconds))}
              </span>
              <input
                type="range"
                min="0"
                max={Math.max(300, scenario.durationSeconds)}
                step="1"
                value={scenario.durationSeconds}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  onUpdate(scenario.id, { durationSeconds: val });
                }}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
            </div>
          </div>
        </>
      )}
    </article>
  );
}
